import type { TranslationKey } from '../i18n';
import type { UUID } from './common';

export const BILLING_PLAN_KEYS = ['starter', 'growth', 'pro'] as const;
export type BillingPlanKey = (typeof BILLING_PLAN_KEYS)[number];

export const BILLING_PLAN_RANK: Record<BillingPlanKey, number> = {
  starter: 1,
  growth: 2,
  pro: 3,
} as const;

export const BILLING_INTERVALS = ['monthly', 'annual'] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];

export type PayPalEnvironment = 'sandbox' | 'live';
export type BillingAccessMode = 'pilot' | 'paypal';
export type BillingAccessSource = 'none' | 'paypal' | 'pilot';

export const BILLING_STATES = [
  'missing',
  'configuration_error',
  'approval_pending',
  'active',
  'grace',
  'cancelled_pending_period_end',
  'cancelled',
  'limited',
  'suspended',
  'expired',
] as const;
export type BillingState = (typeof BILLING_STATES)[number];

export const BILLING_CURRENT_PLAN_STATES = [
  'active',
  'grace',
  'cancelled_pending_period_end',
] as const satisfies readonly BillingState[];

export const BILLING_CANCELLABLE_STATES = [
  'active',
  'grace',
  'limited',
  'suspended',
] as const satisfies readonly BillingState[];

export const BILLING_STATE_TRANSLATION_KEYS: Record<BillingState, TranslationKey> = {
  missing: 'admin.billing.status.missing',
  configuration_error: 'admin.billing.status.configurationError',
  approval_pending: 'admin.billing.status.approvalPending',
  active: 'admin.billing.status.active',
  grace: 'admin.billing.status.grace',
  cancelled_pending_period_end: 'admin.billing.status.cancelledPendingPeriodEnd',
  cancelled: 'admin.billing.status.cancelled',
  limited: 'admin.billing.status.limited',
  suspended: 'admin.billing.status.suspended',
  expired: 'admin.billing.status.expired',
};

export const PAYPAL_SUBSCRIPTION_STATUSES = [
  'APPROVAL_PENDING',
  'APPROVED',
  'ACTIVE',
  'SUSPENDED',
  'CANCELLED',
  'EXPIRED',
] as const;
export type PayPalSubscriptionStatus = (typeof PAYPAL_SUBSCRIPTION_STATUSES)[number];

export const PAYPAL_WEBHOOK_HANDLED_EVENTS = [
  'BILLING.SUBSCRIPTION.CREATED',
  'BILLING.SUBSCRIPTION.ACTIVATED',
  'BILLING.SUBSCRIPTION.UPDATED',
  'BILLING.SUBSCRIPTION.CANCELLED',
  'BILLING.SUBSCRIPTION.SUSPENDED',
  'BILLING.SUBSCRIPTION.EXPIRED',
  'BILLING.SUBSCRIPTION.PAYMENT.FAILED',
  'PAYMENT.SALE.COMPLETED',
  'PAYMENT.SALE.DENIED',
  'PAYMENT.SALE.REFUNDED',
  'PAYMENT.SALE.REVERSED',
] as const;
export type PayPalWebhookHandledEvent = (typeof PAYPAL_WEBHOOK_HANDLED_EVENTS)[number];

export const PAYPAL_PAYMENT_INVALIDATION_EVENTS = [
  'PAYMENT.SALE.DENIED',
  'PAYMENT.SALE.REFUNDED',
  'PAYMENT.SALE.REVERSED',
] as const satisfies readonly PayPalWebhookHandledEvent[];

export type PromotionToolsLevel = 'basic_store_link' | 'full';
export type AnalyticsLevel = 'basic' | 'advanced';
export type SupportLevel = 'standard' | 'priority';

export interface BillingPlanCatalogItem {
  readonly id: UUID;
  readonly planKey: BillingPlanKey;
  readonly interval: BillingInterval;
  readonly currency: 'ILS';
  readonly amountAgorot: number;
  readonly paypalPlanId: string;
  readonly paypalEnvironment: PayPalEnvironment;
  readonly activeProductLimit: number;
  readonly mediaStorageBytes: number;
  readonly aiImageEditsPerPeriod: number;
  readonly promotionToolsLevel: PromotionToolsLevel;
  readonly analyticsLevel: AnalyticsLevel;
  readonly supportLevel: SupportLevel;
}

export interface PaidPeriodBasis {
  readonly id: UUID;
  readonly storeId: UUID;
  readonly subscriptionId: UUID;
  readonly paypalEnvironment: PayPalEnvironment;
  readonly paypalSubscriptionId: string;
  readonly paypalTransactionId: string | null;
  readonly paypalEventId: string | null;
  readonly basisSource: 'subscription_fetch' | 'webhook_event' | 'reconcile';
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly amountAgorot: number;
  readonly currency: 'ILS';
}

export interface StoreBillingSubscription {
  readonly id: UUID;
  readonly storeId: UUID;
  readonly state: BillingState;
  readonly planKey: BillingPlanKey | null;
  readonly interval: BillingInterval | null;
  readonly currency: 'ILS' | null;
  readonly paypalEnvironment: PayPalEnvironment | null;
  readonly paypalSubscriptionId: string | null;
  readonly paypalPlanId: string | null;
  readonly paypalStatus: PayPalSubscriptionStatus | string | null;
  readonly currentPeriodStart: string | null;
  readonly currentPeriodEnd: string | null;
  readonly paidThroughAt: string | null;
  readonly latestPaidBasisId: UUID | null;
  readonly cancelAtPeriodEnd: boolean;
  readonly graceEndsAt: string | null;
  readonly pendingPlanKey: BillingPlanKey | null;
  readonly pendingInterval: BillingInterval | null;
  readonly pendingEffectiveAt: string | null;
  readonly pendingRevisionIntentId: UUID | null;
  readonly lastVerifiedPayPalEventAt: string | null;
  readonly lastPayPalSyncAt: string | null;
}

export interface BillingUsagePeriod {
  readonly id: UUID;
  readonly storeId: UUID;
  readonly subscriptionId: UUID | null;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly aiImageEditsUsed: number;
}

export interface PilotAccessGrant {
  readonly id: UUID;
  readonly storeId: UUID;
  readonly planKey: Extract<BillingPlanKey, 'pro'>;
  readonly accessStartsAt: string;
  readonly accessEndsAt: string;
  readonly revokedAt: string | null;
}

export interface EntitlementSnapshot {
  readonly storeId: UUID;
  readonly state: BillingState;
  readonly planKey: BillingPlanKey | null;
  readonly interval: BillingInterval | null;
  readonly paidAccessUntil: string | null;
  readonly graceEndsAt: string | null;
  readonly hasVerifiedPaidBasis: boolean;
  readonly hasActivePilotAccess: boolean;
  readonly accessSource: BillingAccessSource;
  readonly pilotAccessUntil: string | null;
  readonly canUsePaidActions: boolean;
  readonly activeProductLimit: number;
  readonly activeProductsUsed: number;
  readonly mediaStorageBytes: number;
  readonly mediaStorageBytesUsed: number;
  readonly aiImageEditsPerPeriod: number;
  readonly aiImageEditsUsed: number;
  readonly promotionToolsLevel: PromotionToolsLevel | null;
  readonly analyticsLevel: AnalyticsLevel | null;
  readonly supportLevel: SupportLevel | null;
  readonly overages: readonly EntitlementOverage[];
}

export const ENTITLEMENT_GATES = [
  'store_fulfillment',
  'active_product_create',
  'active_product_publish',
  'media_storage_increase',
  'ai_image_edit',
  'promotion_kit_paid_tools',
  'promotion_link_basic_share',
  'promotion_analytics',
  'advanced_analytics',
  'priority_support_indicator',
] as const;
export type EntitlementGate = (typeof ENTITLEMENT_GATES)[number];

export type EntitlementBlockReason =
  | 'missing_subscription'
  | 'configuration_error'
  | 'activation_pending'
  | 'payment_required'
  | 'suspended'
  | 'expired'
  | 'cancelled'
  | 'product_limit_reached'
  | 'storage_limit_reached'
  | 'ai_quota_exhausted'
  | 'plan_upgrade_required'
  | 'permission_denied'
  | 'owner_required'
  | 'paid_basis_missing'
  | 'environment_mismatch';

export interface EntitlementGateResult {
  readonly allowed: boolean;
  readonly reason: EntitlementBlockReason | null;
  readonly messageKey: TranslationKey | null;
  readonly upgradeTargetPlanKey: BillingPlanKey | null;
}

export interface EntitlementOverage {
  readonly gate: 'active_products' | 'media_storage' | 'ai_image_edits';
  readonly used: number;
  readonly limit: number;
}

export interface StartSubscriptionInput {
  readonly storeId: UUID;
  readonly planKey: BillingPlanKey;
  readonly interval: BillingInterval;
}

export interface StartSubscriptionResult {
  readonly intentId: UUID;
  readonly paypalPlanId: string;
  readonly paypalEnvironment: PayPalEnvironment;
  readonly expiresAt: string;
  readonly customId: string;
}

export interface ConfirmSubscriptionInput {
  readonly storeId: UUID;
  readonly intentId: UUID;
  readonly paypalSubscriptionId: string;
}

export interface ChangeSubscriptionInput {
  readonly storeId: UUID;
  readonly planKey: BillingPlanKey;
  readonly interval: BillingInterval;
}

export interface ChangeSubscriptionResult {
  readonly revisionIntentId: UUID;
  readonly approvalUrl: string;
  readonly pendingEffectiveAt: string | null;
}

export interface ConfirmRevisionInput {
  readonly storeId: UUID;
  readonly revisionIntentId: UUID;
}

export interface BillingSummary {
  readonly catalog: readonly BillingPlanCatalogItem[];
  readonly subscription: StoreBillingSubscription | null;
  readonly pilotGrant: PilotAccessGrant | null;
  readonly accessMode: BillingAccessMode;
  readonly usage: BillingUsagePeriod | null;
  readonly entitlement: EntitlementSnapshot;
  readonly isOwner: boolean;
  readonly paypalClientIdConfigured: boolean;
  readonly serverConfigured: boolean;
}

export interface BillingDiagnostics {
  readonly storeId: UUID;
  readonly latestState: BillingState;
  readonly paypalEnvironment: PayPalEnvironment | null;
  readonly paypalSubscriptionId: string | null;
  readonly latestPayPalStatus: string | null;
  readonly latestEventType: string | null;
  readonly latestEventCreatedAt: string | null;
  readonly latestEventAppliedAt: string | null;
  readonly lastSyncAt: string | null;
  readonly entitlement: EntitlementSnapshot;
}

export type BillingErrorKey =
  | 'admin.billing.errors.permissionDenied'
  | 'admin.billing.errors.paypalUnavailable'
  | 'admin.billing.errors.configurationMissing'
  | 'admin.billing.errors.activationPending'
  | 'admin.billing.errors.planMismatch'
  | 'admin.billing.errors.planAlreadyActive'
  | 'admin.billing.errors.reconsentRequired'
  | 'admin.billing.errors.webhookUnverified'
  | 'admin.billing.errors.paymentPaused'
  | 'admin.billing.errors.retry'
  | 'admin.entitlements.reasons.missingSubscription'
  | 'admin.entitlements.reasons.configurationError'
  | 'admin.entitlements.reasons.activationPending'
  | 'admin.entitlements.reasons.paymentRequired'
  | 'admin.entitlements.reasons.suspended'
  | 'admin.entitlements.reasons.expired'
  | 'admin.entitlements.reasons.cancelled'
  | 'admin.entitlements.reasons.productLimitReached'
  | 'admin.entitlements.reasons.storageLimitReached'
  | 'admin.entitlements.reasons.aiQuotaExhausted'
  | 'admin.entitlements.reasons.planUpgradeRequired'
  | 'admin.entitlements.reasons.permissionDenied'
  | 'admin.entitlements.reasons.ownerRequired'
  | 'admin.entitlements.reasons.paidBasisMissing'
  | 'admin.entitlements.reasons.environmentMismatch'
  | 'common.errors.networkFailed'
  | 'common.errors.unknown';
